import React from 'react';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

import Input from '../../../Template/layoutComponents/Input';
import Select from '../../../Template/layoutComponents/SingleSelect';
import Checkbox from '../../../Template/layoutComponents/Checkmark';
import {
  instrumentCategorySchema,
  defaultInstrumentCategory,
  createEmptyReadingProperty,
  createEmptyProperty,
} from '../../schemas/instrumentCategorySchema';

import './style.css';

const InstrumentCategoryForm = ({ 
  initialData = defaultInstrumentCategory, 
  onSubmit, 
  isLoading = false,
  onCancel 
}) => {
  console.log('🔍 InstrumentCategoryForm renderizado com props:', { initialData, onSubmit, isLoading, onCancel });
  const { t } = useTranslation();
  
  const {
    control,
    handleSubmit,
    watch,
    formState: { errors, isValid },
  } = useForm({
    resolver: zodResolver(instrumentCategorySchema),
    defaultValues: initialData,
    mode: 'onChange',
  });

  const {
    fields: readingPropertiesFields,
    append: appendReadingProperty,
    remove: removeReadingProperty,
  } = useFieldArray({
    control,
    name: 'readingProperties',
  });

  const {
    fields: propertiesFields,
    append: appendProperty,
    remove: removeProperty,
  } = useFieldArray({
    control,
    name: 'properties',
  });

  const watchedProperties = watch('properties');

  const typeOptions = [
    { id: 'string', label: t('text') || 'Texto' },
    { id: 'number', label: t('number') || 'Número' },
    { id: 'enum', label: t('enum') || 'Enum' },
  ];

  const renderReadingProperties = () => (
    <div className="form-section">
      <div className="section-header">
        <h3>{t('reading_properties') || 'Propriedades de Leitura'}</h3>
        <Button
          type="dashed"
          onClick={() => appendReadingProperty(createEmptyReadingProperty())}
          icon={<PlusOutlined />}
        >
          {t('add_reading_property') || 'Adicionar Propriedade de Leitura'}
        </Button>
      </div>

      {readingPropertiesFields.map((field, index) => (
        <ReadingPropertyItem
          key={field.id}
          index={index}
          control={control}
          errors={errors}
          onRemove={() => removeReadingProperty(index)}
          canRemove={readingPropertiesFields.length > 1}
          t={t}
        />
      ))}
    </div>
  );

  const renderProperties = () => (
    <div className="form-section">
      <div className="section-header">
        <h3>{t('properties') || 'Propriedades'}</h3>
        <Button
          type="dashed"
          onClick={() => appendProperty(createEmptyProperty())}
          icon={<PlusOutlined />}
        >
          {t('add_property') || 'Adicionar Propriedade'}
        </Button>
      </div>

      {propertiesFields.map((field, index) => (
        <PropertyItem
          key={field.id}
          index={index}
          control={control}
          errors={errors}
          watchedType={watchedProperties?.[index]?.type}
          onRemove={() => removeProperty(index)}
          typeOptions={typeOptions}
          t={t}
        />
      ))}
    </div>
  );

  console.log('🔍 InstrumentCategoryForm retornando JSX, errors:', errors);
  console.log('🔍 readingPropertiesFields:', readingPropertiesFields);
  console.log('🔍 propertiesFields:', propertiesFields);

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="instrument-category-form">
      <div className="form-section">
        <div className="form-field">
          <label>{t('category_name') || 'Nome da Categoria'} *</label>
          <Controller
            name="name"
            control={control}
            render={({ field }) => (
              <Input
                {...field}
                placeholder={t('enter_category_name') || 'Digite o nome da categoria'}
                error={errors.name?.message}
              />
            )}
          />
          {errors.name && <span className="error-message">{errors.name.message}</span>}
        </div>
      </div>

      {renderReadingProperties()}
      {renderProperties()}

      <div className="form-actions">
        {onCancel && (
          <Button 
            type="default" 
            onClick={onCancel}
            disabled={isLoading}
          >
            {t('cancel') || 'Cancelar'}
          </Button>
        )}
        <Button
          type="primary"
          htmlType="submit"
          loading={isLoading}
          disabled={!isValid}
        >
          {t('save') || 'Salvar'}
        </Button>
      </div>
    </form>
  );
};

const ReadingPropertyItem = ({ index, control, errors, onRemove, canRemove, t }) => {
  const {
    fields: unitsFields,
    append: appendUnit,
    remove: removeUnit,
  } = useFieldArray({
    control,
    name: `readingProperties.${index}.units`,
  });

  return (
    <div className="property-item">
      <div className="property-header">
        <h4>{t('reading_property') || 'Propriedade de Leitura'} {index + 1}</h4>
        {canRemove && (
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={onRemove}
          />
        )}
      </div>

      <div className="form-field">
        <label>{t('property_name') || 'Nome da Propriedade'} *</label>
        <Controller
          name={`readingProperties.${index}.name`}
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              placeholder={t('enter_property_name') || 'Digite o nome da propriedade'}
              error={errors.readingProperties?.[index]?.name?.message}
            />
          )}
        />
        {errors.readingProperties?.[index]?.name && (
          <span className="error-message">
            {errors.readingProperties[index].name.message}
          </span>
        )}
      </div>

      <div className="units-section">
        <div className="section-header">
          <label>{t('units') || 'Unidades'} *</label>
          <Button
            type="dashed"
            size="small"
            onClick={() => appendUnit('')}
            icon={<PlusOutlined />}
          >
            {t('add_unit') || 'Adicionar Unidade'}
          </Button>
        </div>

        {unitsFields.map((unitField, unitIndex) => (
          <div key={unitField.id} className="unit-item">
            <Controller
              name={`readingProperties.${index}.units.${unitIndex}`}
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  placeholder={t('enter_unit_name') || 'Digite o nome da unidade'}
                  error={errors.readingProperties?.[index]?.units?.[unitIndex]?.message}
                />
              )}
            />
            {unitsFields.length > 1 && (
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                onClick={() => removeUnit(unitIndex)}
              />
            )}
          </div>
        ))}
        
        {errors.readingProperties?.[index]?.units && (
          <span className="error-message">
            {errors.readingProperties[index].units.message}
          </span>
        )}
      </div>
    </div>
  );
};

const PropertyItem = ({ index, control, errors, watchedType, onRemove, typeOptions, t }) => {
  const {
    fields: enumFields,
    append: appendEnum,
    remove: removeEnum,
  } = useFieldArray({
    control,
    name: `properties.${index}.enumValues`,
  });

  return (
    <div className="property-item">
      <div className="property-header">
        <h4>{t('property') || 'Propriedade'} {index + 1}</h4>
        <Button
          type="text"
          danger
          icon={<DeleteOutlined />}
          onClick={onRemove}
        />
      </div>

      <div className="form-field">
        <label>{t('property_name') || 'Nome da Propriedade'} *</label>
        <Controller
          name={`properties.${index}.name`}
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              placeholder={t('enter_property_name') || 'Digite o nome da propriedade'}
              error={errors.properties?.[index]?.name?.message}
            />
          )}
        />
        {errors.properties?.[index]?.name && (
          <span className="error-message">
            {errors.properties[index].name.message}
          </span>
        )}
      </div>

      <div className="form-field">
        <label>{t('property_type') || 'Tipo da Propriedade'} *</label>
        <Controller
          name={`properties.${index}.type`}
          control={control}
          render={({ field }) => (
            <Select
              value={{ id: field.value }}
              options={typeOptions}
              onChange={(e) => field.onChange(e.target.value)}
              error={errors.properties?.[index]?.type?.message}
            />
          )}
        />
        {errors.properties?.[index]?.type && (
          <span className="error-message">
            {errors.properties[index].type.message}
          </span>
        )}
      </div>

      <div className="form-field">
        <Controller
          name={`properties.${index}.required`}
          control={control}
          render={({ field }) => (
            <Checkbox
              checked={field.value}
              onChange={(e) => field.onChange(e.target.checked)}
              label={t('required_field') || 'Campo Obrigatório'}
            />
          )}
        />
      </div>

      {watchedType === 'enum' && (
        <div className="enum-section">
          <div className="section-header">
            <label>{t('enum_values') || 'Valores do Enum'} *</label>
            <Button
              type="dashed"
              size="small"
              onClick={() => appendEnum('')}
              icon={<PlusOutlined />}
            >
              {t('add_enum_value') || 'Adicionar Valor'}
            </Button>
          </div>

          {enumFields.map((enumField, enumIndex) => (
            <div key={enumField.id} className="enum-item">
              <Controller
                name={`properties.${index}.enumValues.${enumIndex}`}
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    placeholder={t('enter_enum_value') || 'Digite o valor do enum'}
                    error={errors.properties?.[index]?.enumValues?.[enumIndex]?.message}
                  />
                )}
              />
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                onClick={() => removeEnum(enumIndex)}
              />
            </div>
          ))}
          
          {errors.properties?.[index]?.enumValues && (
            <span className="error-message">
              {errors.properties[index].enumValues.message}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default InstrumentCategoryForm;